// purpose: iOS Safari で停止する Web Audio を迂回し、事前レンダーBGMとゲームSEを HTMLMediaElement だけで確実に鳴らす。

import { R2_MINIMAL_STEP_SECONDS } from './r2/NearFutureMusicProfile';
import type { AudioSettings } from './AudioEngine';

const MUSIC_URLS = [
  '/assets/audio/r2/music/stage-1-loop.wav',
  '/assets/audio/r2/music/stage-2-loop.wav',
  '/assets/audio/r2/music/stage-3-loop.wav',
  '/assets/audio/r2/music/boss-loop.wav',
] as const;

const SFX_URLS = [
  '/assets/audio/r2/techno/event-00-pickup.wav',
  '/assets/audio/r2/player-shot-a-01.wav',
  '/assets/audio/r2/techno/event-02-small-kill.wav',
  '/assets/audio/r2/techno/event-03-power-down.wav',
  '/assets/audio/r2/techno/event-04-large-kill.wav',
  '/assets/audio/r2/techno/event-05-laser.wav',
  '/assets/audio/r2/techno/event-06-ricochet.wav',
  '/assets/audio/r2/techno/event-07-tether.wav',
  '/assets/audio/r2/techno/event-08-device-repair.wav',
  '/assets/audio/r2/techno/event-09-device-phase.wav',
  '/assets/audio/r2/techno/event-10-device-time.wav',
  '/assets/audio/r2/techno/event-11-device-overdrive.wav',
  '/assets/audio/r2/techno/event-12-boss-spawn.wav',
  '/assets/audio/r2/techno/event-13-boss-phase.wav',
  '/assets/audio/r2/techno/event-14-boss-defeat.wav',
  '/assets/audio/r2/techno/event-15-player-down.wav',
  '/assets/audio/r2/techno/event-16-gate-open.wav',
  '/assets/audio/r2/techno/event-17-purchase-ok.wav',
  '/assets/audio/r2/techno/event-18-purchase-fail.wav',
  '/assets/audio/r2/techno/event-19-weapon-level.wav',
] as const;

const PICKUP_ID = 0;
const SHOT_ID = 1;
const SMALL_KILL_ID = 2;
const HEAVY_IDS = new Set([12, 14, 15]);
const COMBO_URLS = [
  '/assets/audio/r2/techno/combo-rush.wav',
  '/assets/audio/r2/techno/combo-storm.wav',
  '/assets/audio/r2/techno/combo-overdrive.wav',
  '/assets/audio/r2/techno/combo-break.wav',
] as const;
const IGNORE_MEDIA_PLAY_REJECTION = (): void => undefined;

export function needsHtmlAudioFallback(): boolean {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent)
    || (/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1);
}

function createMedia(url: string, loop = false): HTMLAudioElement {
  const media = new Audio(url);
  media.preload = 'auto';
  media.loop = loop;
  media.setAttribute('playsinline', '');
  media.setAttribute('webkit-playsinline', '');
  return media;
}

export class HtmlAudioFallback {
  // Two reusable decks keep the current track audible until the requested
  // replacement has actually started. Four permanent track elements would
  // multiply iOS media pipelines; one deck cannot switch source without a gap.
  private readonly musicDecks = [
    createMedia(MUSIC_URLS[0], true),
    createMedia(MUSIC_URLS[3], true),
  ] as const;
  // iOS creates a complete media pipeline per HTMLAudioElement. The shot is
  // shorter than the legal fire cooldown, so one retriggerable channel is
  // enough. Pickup and small-kill alternate heavily during combat, so those two
  // tiny samples keep fixed pipelines while every other event remains on the
  // original generic channel. activeEvent still enforces one non-shot voice.
  private readonly shot = createMedia('/assets/audio/r2/player-shot-loop.wav', true);
  private readonly event = createMedia(SFX_URLS[0]);
  private readonly pickupEvent = createMedia(SFX_URLS[PICKUP_ID]);
  private readonly smallKillEvent = createMedia(SFX_URLS[SMALL_KILL_ID]);
  private activeEvent: HTMLAudioElement | null = null;
  private disposed = false;
  private startGeneration = 0;
  private settings: AudioSettings = { master: 0.78, music: 0.82, sfx: 0.7 };
  private activeMusicDeck = 0;
  private currentMusicIndex = 0;
  private desiredMusicIndex = 0;
  private pendingMusicIndex = -1;
  private queuedMusicPrepareIndex = -1;
  private musicSwitchGeneration = 0;
  private musicSwitchRetryFrames = 0;
  private musicSwitchNeedsGesture = false;
  private shotNeedsGesture = false;
  private shotPlayGeneration = 0;
  private currentLayer = 0;
  private started = false;
  private paused = false;
  private duckLevel = 1;
  private duckTimer: number | null = null;
  private playerFireHeld = false;
  private shotStopTimer: number | null = null;

  get ready(): boolean {
    return this.started && !this.paused && !this.activeMusic.paused;
  }

  get needsUserGesture(): boolean {
    return this.started
      && !this.paused
      && (
        this.activeMusic.paused
        || this.musicSwitchNeedsGesture
        || this.shotNeedsGesture
      );
  }

  get state(): string {
    return this.ready ? 'running' : 'suspended';
  }

  async ensureStarted(): Promise<void> {
    if (this.disposed) return;
    const generation = ++this.startGeneration;
    // Preserve which failed voices specifically need this real gesture. Their
    // play() calls must be issued before the first await while WebKit still
    // exposes transient user activation.
    const retryMusicSwitch = this.musicSwitchNeedsGesture;
    const retryHeldShot = this.shotNeedsGesture;
    this.paused = false;
    this.musicSwitchNeedsGesture = false;
    this.musicSwitchRetryFrames = 0;
    this.shotNeedsGesture = false;
    this.applyMusicVolume();
    // `load()` must run before the first await so WebKit associates both added
    // media elements with the same explicit SOUND ON gesture as the music.
    // Reissue it on every explicit resume gesture: iOS can discard a media
    // pipeline while the page is backgrounded even though the JS object lives.
    // Never reload a voice that is already audible. In particular, reloading
    // the held-shot loop loses its only rising edge and leaves it silent until
    // the player releases and presses FIRE again.
    this.primeMediaIfIdle(this.shot);
    this.primeMediaIfIdle(this.event);
    this.primeMediaIfIdle(this.pickupEvent);
    this.primeMediaIfIdle(this.smallKillEvent);
    this.primeMediaIfIdle(this.standbyMusic);
    const music = this.activeMusic;
    const activeMusicPlay = music.play();
    if (
      retryMusicSwitch
      && this.started
      && this.desiredMusicIndex !== this.currentMusicIndex
      && this.pendingMusicIndex < 0
    ) {
      this.switchMusic(this.desiredMusicIndex);
    }
    if (retryHeldShot && this.started && this.playerFireHeld) {
      this.startHeldShot();
    }
    try {
      await activeMusicPlay;
    } catch (error) {
      if (this.disposed || generation !== this.startGeneration) return;
      throw error;
    }
    // A stale unlock continuation must not pause the shared music element:
    // a newer gesture may already have started it successfully.
    if (this.disposed || generation !== this.startGeneration) return;
    this.started = true;
    // FIRE can become held while music.play() is pending. Restore the loop
    // after the await so that edge-gated input cannot strand the shot in a
    // permanently paused state.
    this.startHeldShot();
    if (this.desiredMusicIndex !== this.currentMusicIndex) {
      this.switchMusic(this.desiredMusicIndex);
    }
  }

  setSettings(settings: AudioSettings): void {
    this.settings = settings;
    this.applyMusicVolume();
  }

  prepareStageMusic(stage: number): void {
    this.queuedMusicPrepareIndex = Math.max(0, Math.min(2, stage | 0));
  }

  setPaused(value: boolean): void {
    this.paused = value;
    if (value) {
      this.startGeneration += 1;
      this.shotPlayGeneration += 1;
      this.cancelMusicSwitch();
      for (const music of this.musicDecks) music.pause();
      this.shot.pause();
      this.stopActiveEvent();
      return;
    }
    if (this.started) void this.activeMusic.play().catch(IGNORE_MEDIA_PLAY_REJECTION);
    this.startHeldShot();
  }

  update(layer: number, boss: boolean, stage: number): void {
    const nextLayer = Math.max(0, Math.min(3, layer | 0));
    const layerChanged = nextLayer !== this.currentLayer;
    this.currentLayer = nextLayer;
    const index = boss ? 3 : Math.max(0, Math.min(2, stage | 0));
    this.desiredMusicIndex = index;
    if (index === this.currentMusicIndex) {
      this.musicSwitchNeedsGesture = false;
      this.musicSwitchRetryFrames = 0;
    }
    if (this.pendingMusicIndex >= 0 && this.pendingMusicIndex !== index) {
      this.cancelMusicSwitch();
    }
    if (this.musicSwitchRetryFrames > 0) this.musicSwitchRetryFrames -= 1;
    if (
      this.started
      && !this.paused
      && index !== this.currentMusicIndex
      && this.pendingMusicIndex < 0
      && this.musicSwitchRetryFrames === 0
    ) {
      this.switchMusic(index);
    } else if (layerChanged) {
      this.applyMusicVolume();
    }
    // This is intentionally unconditional. prepareQueuedMusic() owns the
    // stability guards, and calling it after target cancellation also covers a
    // boss→result transition where no replacement switch needs to commit.
    this.prepareQueuedMusic();
  }

  playSfx(id: number): void {
    if (!this.started || this.paused) return;
    const url = SFX_URLS[id];
    if (!url) return;
    if (id === SHOT_ID && this.playerFireHeld) {
      // Recover from a rejected/aborted held-loop start without retriggering a
      // healthy loop on every projectile event.
      this.startHeldShot();
      return;
    }
    const media = id === SHOT_ID
      ? this.shot
      : id === PICKUP_ID
        ? this.pickupEvent
        : id === SMALL_KILL_ID
          ? this.smallKillEvent
          : this.event;
    if (id === SHOT_ID) {
      this.shotPlayGeneration += 1;
      this.shotNeedsGesture = false;
      media.pause();
    } else {
      this.stopActiveEvent();
      this.activeEvent = media;
    }
    if (!media.src.endsWith(url)) {
      media.src = url;
      media.load();
    }
    media.currentTime = 0;
    const base = id === SHOT_ID ? 0.46 : HEAVY_IDS.has(id) ? 0.76 : id === 0 ? 0.62 : 0.66;
    media.volume = Math.max(0, Math.min(1, this.settings.master * this.settings.sfx * base));
    void media.play().catch(IGNORE_MEDIA_PLAY_REJECTION);
    if (HEAVY_IDS.has(id)) this.duck(id === 14 ? 0.42 : 0.62, id === 14 ? 0.72 : 0.34);
  }

  setPlayerFireHeld(active: boolean): void {
    if (this.playerFireHeld === active) return;
    this.playerFireHeld = active;
    if (this.shotStopTimer !== null) {
      window.clearTimeout(this.shotStopTimer);
      this.shotStopTimer = null;
    }
    if (active) {
      this.startHeldShot();
      return;
    }
    this.shotPlayGeneration += 1;
    this.shotNeedsGesture = false;
    // Preserve the attack of a very short tap, but stop before the next 134 BPM
    // sixteenth begins.
    this.shotStopTimer = window.setTimeout(() => {
      this.shot.pause();
      this.shot.currentTime = 0;
      this.shotStopTimer = null;
    }, 88);
  }

  playCombo(index: number): void {
    if (!this.started || this.paused) return;
    const url = COMBO_URLS[index];
    if (!url) return;
    this.stopActiveEvent();
    this.activeEvent = this.event;
    this.event.src = url;
    this.event.load();
    this.event.currentTime = 0;
    this.event.volume = Math.max(0, Math.min(1, this.settings.master * this.settings.sfx * 0.58));
    void this.event.play().catch(IGNORE_MEDIA_PLAY_REJECTION);
  }

  duck(target: number, duration: number): void {
    this.duckLevel = Math.max(0.2, Math.min(1, target));
    this.applyMusicVolume();
    if (this.duckTimer !== null) window.clearTimeout(this.duckTimer);
    this.duckTimer = window.setTimeout(() => {
      this.duckLevel = 1;
      this.duckTimer = null;
      this.applyMusicVolume();
    }, Math.max(30, duration * 1000));
  }

  stepPosition(): number {
    if (!this.ready || this.paused) return -1;
    return Math.max(0, this.activeMusic.currentTime / R2_MINIMAL_STEP_SECONDS);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.startGeneration += 1;
    this.musicSwitchGeneration += 1;
    this.shotPlayGeneration += 1;
    if (this.duckTimer !== null) window.clearTimeout(this.duckTimer);
    if (this.shotStopTimer !== null) window.clearTimeout(this.shotStopTimer);
    for (const music of this.musicDecks) {
      music.pause();
      music.removeAttribute('src');
    }
    for (const media of [this.shot, this.event, this.pickupEvent, this.smallKillEvent]) {
      media.pause();
      media.removeAttribute('src');
    }
    this.activeEvent = null;
    this.started = false;
  }

  private switchMusic(index: number): void {
    if (
      this.disposed
      || !this.started
      || this.paused
      || index === this.currentMusicIndex
      || this.pendingMusicIndex >= 0
    ) return;
    const url = MUSIC_URLS[index];
    if (!url) return;
    const current = this.activeMusic;
    const nextDeck = this.activeMusicDeck === 0 ? 1 : 0;
    const next = this.musicDecks[nextDeck];
    const generation = ++this.musicSwitchGeneration;
    this.pendingMusicIndex = index;
    next.pause();
    if (!next.src.endsWith(url)) {
      next.src = url;
      next.load();
    }
    next.currentTime = 0;
    this.applyMusicVolume();
    void next.play().then(
      () => {
        // A newer target, pause, or dispose owns both decks now. Its path is
        // responsible for stopping anything it no longer needs.
        if (
          this.disposed
          || generation !== this.musicSwitchGeneration
          || this.paused
          || this.desiredMusicIndex !== index
        ) return;
        // Commit only after the replacement is audibly running. The old deck
        // covers decode/buffer latency and remains the fallback on rejection.
        current.pause();
        current.currentTime = 0;
        this.activeMusicDeck = nextDeck;
        this.currentMusicIndex = index;
        this.pendingMusicIndex = -1;
        this.musicSwitchRetryFrames = 0;
        this.musicSwitchNeedsGesture = false;
        this.applyMusicVolume();
        this.prepareQueuedMusic();
      },
      (error: unknown) => {
        if (this.disposed || generation !== this.musicSwitchGeneration) return;
        next.pause();
        next.currentTime = 0;
        this.pendingMusicIndex = -1;
        this.musicSwitchNeedsGesture = this.isNotAllowedError(error);
        // Avoid a rejected play() hot-loop; the old deck remains audible while
        // the same target is retried after a short fixed-frame backoff.
        this.musicSwitchRetryFrames = this.musicSwitchNeedsGesture ? -1 : 30;
      },
    );
  }

  private cancelMusicSwitch(): void {
    if (this.pendingMusicIndex < 0) return;
    this.musicSwitchGeneration += 1;
    this.standbyMusic.pause();
    this.standbyMusic.currentTime = 0;
    this.pendingMusicIndex = -1;
    this.musicSwitchRetryFrames = 0;
    this.musicSwitchNeedsGesture = false;
  }

  private prepareQueuedMusic(): void {
    const index = this.queuedMusicPrepareIndex;
    if (index < 0) return;
    if (index === this.currentMusicIndex) {
      this.queuedMusicPrepareIndex = -1;
      return;
    }
    // StageCleared is emitted before the frame's audio.update observes
    // boss=false. Preserve the already-buffered current-stage deck until the
    // boss→stage handoff commits, then reuse the old boss deck for next-stage
    // preparation.
    if (
      this.currentMusicIndex === 3
      || this.desiredMusicIndex !== this.currentMusicIndex
      || this.pendingMusicIndex >= 0
    ) return;
    const url = MUSIC_URLS[index];
    if (!url) return;
    const standby = this.standbyMusic;
    standby.pause();
    if (!standby.src.endsWith(url)) {
      standby.src = url;
      standby.load();
    }
    standby.currentTime = 0;
    this.queuedMusicPrepareIndex = -1;
  }

  private stopActiveEvent(): void {
    const active = this.activeEvent;
    if (!active) return;
    active.pause();
    active.currentTime = 0;
    this.activeEvent = null;
  }

  private primeMediaIfIdle(media: HTMLAudioElement): void {
    if (!media.paused) return;
    media.load();
  }

  private isNotAllowedError(error: unknown): boolean {
    return Boolean(
      error
      && typeof error === 'object'
      && 'name' in error
      && error.name === 'NotAllowedError',
    );
  }

  private startHeldShot(): void {
    if (
      !this.started
      || this.paused
      || !this.playerFireHeld
      || !this.shot.paused
      || this.shotNeedsGesture
    ) return;
    this.shot.currentTime = 0;
    this.shot.volume = Math.max(0, Math.min(1, this.settings.master * this.settings.sfx * 0.46));
    const generation = ++this.shotPlayGeneration;
    void this.shot.play().then(
      () => {
        if (
          this.disposed
          || generation !== this.shotPlayGeneration
          || this.paused
          || !this.playerFireHeld
        ) return;
        this.shotNeedsGesture = false;
      },
      (error: unknown) => {
        if (
          this.disposed
          || generation !== this.shotPlayGeneration
          || this.paused
          || !this.playerFireHeld
        ) return;
        this.shotNeedsGesture = this.isNotAllowedError(error);
      },
    );
  }

  private applyMusicVolume(): void {
    const layerLift = 0.88 + this.currentLayer * 0.04;
    const volume = Math.max(
      0,
      Math.min(1, this.settings.master * this.settings.music * 0.92 * layerLift * this.duckLevel),
    );
    for (const music of this.musicDecks) music.volume = volume;
  }

  private get activeMusic(): HTMLAudioElement {
    return this.musicDecks[this.activeMusicDeck];
  }

  private get standbyMusic(): HTMLAudioElement {
    return this.musicDecks[this.activeMusicDeck === 0 ? 1 : 0];
  }
}
